<script setup lang="ts">
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import ListItem from "@/components/ui/ComplexList/ListItem.vue";
import { useLinkDropTarget } from "@/composables/linkDrag";
import type { BoardData } from "@/model/board";
import type { Objective } from "@/model/objective";

import ObjectiveCardAttributes from "./ObjectiveCardAttributes.vue";

const props = defineProps<{
  objective: Objective;
  board: BoardData<"team">;
  index: number;
  selected?: boolean;
}>();

const { onPointerEnter, onPointerLeave } = useLinkDropTarget(
  () => ({ kind: "objective", boardId: props.board.id, id: props.objective.id }),
  {
    // Reject duplicate links from either a sticky or another objective.
    isValid: (source) => {
      if (source.kind === "sticky") {
        return !props.objective.cards.some((card) => card.id === source.id);
      }
      return !props.objective.linkedObjectives.some((linked) => linked.id === source.id);
    },
  },
);
</script>

<template>
  <ListItem
    :class="['list-item-objective', 'menu-list-item', { selected }]"
    clickable
    :active="selected"
    role="option"
    :aria-selected="!!selected"
    tabindex="-1"
    data-testid="team-objective-row"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <template #before>
      <div class="index">
        {{ index }}
      </div>
    </template>

    <!-- First Row -->
    <div class="first-row">
      <BaseTooltip position="bottom">
        <div class="text">{{ objective.text }}</div>
        <template #content>{{ objective.text }}</template>
      </BaseTooltip>
    </div>

    <!-- Second Row -->
    <template #subtitle>
      <div class="second-row">
        <div class="business-value">
          <div class="number">
            {{ objective.bv }}
          </div>
          <BaseTooltip>
            {{ $t("objectives.bv") }}
            <template #content>
              {{ $t("objectives.businessValue") }}
            </template>
          </BaseTooltip>
        </div>
        <MenuItemDelimiter />
        <ObjectiveCardAttributes :objective :board has-status-distribution board-scale />
      </div>
    </template>
  </ListItem>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/board";
@use "@/styles/font";
@use "@/styles/mixins/utils";

$gap: board.len(8px);
$font-size: board.len(14px);

:deep(.list-item-objective .content .title) {
  font-size: unset;
  height: unset;
}

:deep(.list-item-objective .content .subtitle) {
  font-size: unset;
}

:deep(.list-item.list-item) {
  gap: $gap;
  padding: $gap 2 * $gap;
  border: board.len(2px) solid transparent;
  background-color: colors.$background-ui;

  &.clickable {
    &:hover:not(.active) {
      background-color: colors.$background-ui;
      border-color: colors.$border-subtle;
    }

    &.active,
    &.active:hover {
      background-color: colors.$background-ui;
      border-color: colors.$main;
    }

    &.current {
      background-color: colors.$background-ui;
    }
  }

  &.active,
  &.active:hover {
    background-color: colors.$background-ui;

    .content {
      .title {
        color: colors.$text;
      }

      .subtitle {
        color: colors.$text-subtle;
      }
    }
  }

  &:focus-visible {
    outline: board.len(2px) solid colors.$border-focused;
  }
}

.list-item-objective {
  .index {
    font-size: $font-size;
    font-weight: font.$weight-semi-bold;
    color: colors.$text-subtle;
  }

  .first-row {
    display: flex;

    .text {
      @include utils.ellipsis;

      font-size: $font-size;
      font-weight: font.$weight-semi-bold;
    }
  }

  .second-row {
    display: flex;
    align-items: center;
    gap: $gap;
    height: board.len(20px);

    .business-value {
      display: flex;
      align-items: center;
      gap: $gap;
      font-size: $font-size;
      height: 100%;

      .number {
        display: flex;
        height: 100%;
        justify-content: center;
        align-items: center;
        background-color: colors.$background-grey;
        border-radius: board.len(6px);
        width: 3ch;
      }
    }

    :deep(.menu-item-delimiter) {
      width: board.len(1px);
      height: $gap;
      margin: 0;
    }

    :deep(.status-distribution) {
      flex: 1 1 0;
      min-width: 0;
      height: board.len(10px);
    }
  }
}
</style>
